/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';

import ClearAllIcon from '@mui/icons-material/ClearAll';
import { ButtonBase, IconButton, Popover } from '@mui/material';

import { SettingsIcon } from '../../../../components/svg-icons/SettingsIcon';
import { VolumeOffIcon } from '../../../../components/svg-icons/VolumeOffIcon';
import { BasicRoutes } from '../../../../constants/basicRoutes';
import { Namespaces } from '../../../../i18n';
import { ProfileTabs, tabToRouteBinding } from '../../../../pages/AccontProfilePage/AccountProfilePage.consts';
import { RootState } from '../../../../store';
import {
  removeNotification,
  setNotificationRead,
  setNotifications,
  setNotificationsRead,
} from '../../../../store/notifications/actions';
import { useRenderNotificationItem } from '../../../../utils/useRenderNotificationItem';
import { StyledList, useNotificationMenuStyles } from './NotificationMenu.styles';

const mapState = (state: RootState) => ({
  notifications: state.notifications,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

const COLLAPSED_MENU_ITEMS_NUMBER = 5;

export type NotificationMenuProps = PropsFromRedux & {
  id: string;
  anchorEl: HTMLElement | null;
  handleMenuClose: () => void;
};

const showMuteButton = false;

const NotificationMenuComponent = memo<NotificationMenuProps>(props => {
  const { id, anchorEl, handleMenuClose, notifications, dispatch } = props;
  const navigate = useNavigate();

  const classes = useNotificationMenuStyles();
  const { t } = useTranslation([Namespaces.Navigation]);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const renderNotificationItem = useRenderNotificationItem();

  const renderMenu = () => {
    const notificationsLength = notifications.options.length;
    const toRenderLength = isExpanded ? notificationsLength : COLLAPSED_MENU_ITEMS_NUMBER;
    const setNotificationAsRead = ({ id }: { id: string }) => {
      dispatch(setNotificationRead({ id, isRead: true }));
    };
    const onRemoveBtnClick = ({ id }: { id: string }) => {
      dispatch(removeNotification({ id }));
    };
    return notifications.options.reduce(
      (acc, item) => {
        if (acc.length >= toRenderLength) {
          return acc;
        }
        const element: React.ReactElement<any> | undefined = renderNotificationItem({
          onRootClick: handleMenuClose,
          item,
          asSnackbar: false,
          onRemoveBtnClick,
          setNotificationAsRead,
        });
        return element ? acc.concat([element]) : acc;
      },
      [] as Array<React.ReactElement<any> | undefined>,
    );
  };

  const onMarkAllAsReadClick = () => {
    dispatch(setNotificationsRead());
  };

  const onSettingsBtnClick = () => {
    closeMenu();
    void navigate(`${BasicRoutes.PROFILE}/${tabToRouteBinding[ProfileTabs.Notifications]}`);
  };

  const onClearAllClick = () => {
    dispatch(setNotifications([]));
  };

  const closeMenu = () => {
    handleMenuClose();
    setIsExpanded(false);
  };

  const isMarkAllReadButtonVisible = notifications.unreadCount > 0;
  const isSeeMoreBtnVisible = notifications.options.length > COLLAPSED_MENU_ITEMS_NUMBER;
  const isClearAllBtnVisible = notifications.count > 0;

  return (
    <Popover
      open={Boolean(anchorEl)}
      onClose={closeMenu}
      keepMounted
      id={id}
      anchorEl={anchorEl}
      classes={{ paper: classes.paper }}
      className={cx({ [classes.expandedPaper]: isExpanded })}
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <div className={classes.topActions}>
        {showMuteButton ? (
          <IconButton className={classes.muteBtn} size="large">
            <VolumeOffIcon />
            {t('notifications.labels.mute')}
          </IconButton>
        ) : (
          <div />
        )}
        <div className={classes.iconsGroup}>
          {isClearAllBtnVisible && (
            <IconButton className={classes.clearAllBtn} onClick={onClearAllClick} size="large">
              <ClearAllIcon />
            </IconButton>
          )}
          <IconButton className={classes.settingsBtn} onClick={onSettingsBtnClick} size="large">
            <SettingsIcon />
          </IconButton>
        </div>
      </div>
      {notifications.count > 0 ? (
        <StyledList>{renderMenu()}</StyledList>
      ) : (
        <div className={classes.noItemsText}>{t('notifications.no_notifications')}</div>
      )}
      {(isMarkAllReadButtonVisible || isSeeMoreBtnVisible) && (
        <div className={classes.bottomActions}>
          {isMarkAllReadButtonVisible ? (
            <ButtonBase className={classes.markAsReadBtn} onClick={onMarkAllAsReadClick}>
              {t('notifications.labels.mark_all_as_read')}
            </ButtonBase>
          ) : (
            <div />
          )}
          {isSeeMoreBtnVisible ? (
            <ButtonBase className={classes.seeMoreBtn} onClick={() => setIsExpanded(!isExpanded)}>
              {!isExpanded ? t('notifications.labels.see_more') : t('notifications.labels.see_less')}
            </ButtonBase>
          ) : (
            <div />
          )}
        </div>
      )}
    </Popover>
  );
});

export const NotificationMenu = connector(NotificationMenuComponent);
